import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createPredefinedMessage,
  predefinedListQuery,
  predefinedMessageQuery,
  predefinedShellQuery,
  setPredefinedMessageActive,
  updatePredefinedTranslation,
  type PredefinedMessageRow,
} from '../../api/settingsEaseedeskMessageTemplates'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Predefined messages.
 *
 * Element cu element fata de `version-3/easeedesk_settings/chat/
 * predefined_messages/index.php` + `list.php` + `message.php`, si gest cu gest
 * fata de `index-1.js`, `list-1.js` si `message-1.js`.
 *
 * Un mesaj e un rand gol cu traducerile lui alaturi: panoul care se desface
 * arata cate un rand pe limba, si fiecare se editeaza singur - ca in portal.
 */

function Translations({ message, onSaved }: { message: number; onSaved(): void }) {
  const data = useQuery(predefinedMessageQuery(message))
  const [edited, setEdited] = useState<{ language: number; text: string } | null>(null)

  const save = useMutation({
    mutationFn: () => updatePredefinedTranslation(message, edited?.language ?? 0, edited?.text ?? ''),
    onSuccess: () => {
      toast('Message updated.', 'Predefined messages')
      setEdited(null)
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (data.error) {
    return (
      <div className="panel-body">
        <LoadFailed what="The translations" error={data.error} onRetry={() => void data.refetch()} />
      </div>
    )
  }
  if (!data.data) return <Spinner />

  return (
    <div className="panel-body">
      {data.data.translations.map((translation) => (
        <div className="row" key={translation.language_id}>
          <div className="col-sm-11">
            <span className="label label-info">{translation.lang}</span>{' '}
            <span className="font-12">{translation.message}</span>
          </div>
          <div className="col-sm-1">
            <button
              type="button"
              className="btn btn-xs btn-primary"
              onClick={() => setEdited({ language: translation.language_id, text: translation.message })}
            >
              Edit
            </button>
          </div>
          <hr />
        </div>
      ))}

      <Modal
        id="editPredefinedMessageModal"
        labelledBy="editPredefinedMessageModalLabel"
        open={edited !== null}
        onClose={() => setEdited(null)}
        title="Edit message"
      >
        <form
          id="edit-predefined-message-form"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if ((edited?.text ?? '').trim() === '') return
            save.mutate()
          }}
        >
          <div className="form-group">
            <label className="control-label">Message</label>
            <input
              type="text"
              name="message_body"
              className="message-body form-control"
              value={edited?.text ?? ''}
              onChange={(e) => setEdited(edited === null ? null : { ...edited, text: e.target.value })}
              required
            />
          </div>
          <hr className="dotted" />
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={() => setEdited(null)}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              Update
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function NewMessageModal({
  open,
  onClose,
  onSaved,
  companies,
  languages,
}: {
  open: boolean
  onClose(): void
  onSaved(): void
  companies: Array<{ ID: number; name: string }>
  languages: Array<{ id: number; language_name: string; short_name: string }>
}) {
  const [company, setCompany] = useState(0)
  const [texts, setTexts] = useState<Record<number, string>>({})

  useEffect(() => {
    if (!open) return
    setCompany(0)
    setTexts({})
  }, [open])

  const save = useMutation({
    mutationFn: () => createPredefinedMessage(company, texts),
    onSuccess: () => {
      toast('Message created.', 'Predefined messages')
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const written = Object.values(texts).some((t) => t.trim() !== '')

  return (
    <Modal
      id="newMessageModal"
      labelledBy="newMessageModalLabel"
      open={open}
      onClose={onClose}
      title="Add new message"
    >
      <form
        id="predefined-message-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (!written) return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label>Company</label>
          <select
            className="form-control input-sm"
            name="company"
            value={company}
            onChange={(e) => setCompany(Number(e.target.value))}
          >
            <option value={0}>-- All companies --</option>
            {companies.map((row) => (
              <option key={row.ID} value={row.ID}>
                {row.name}
              </option>
            ))}
          </select>
        </div>

        {languages.map((language) => (
          <div className="form-group" key={language.id}>
            <div className="input-group">
              <span className="input-group-addon">{language.short_name}</span>
              <input
                type="text"
                className="form-control"
                name={`message[${language.id}]`}
                value={texts[language.id] ?? ''}
                onChange={(e) => setTexts({ ...texts, [language.id]: e.target.value })}
              />
            </div>
          </div>
        ))}

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={!written || save.isPending}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function PredefinedMessagesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(predefinedShellQuery())
  const list = useQuery(predefinedListQuery())
  const [creating, setCreating] = useState(false)
  const [open, setOpen] = useState<number | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'chat_predefined_messages'] })
  }

  const flip = useMutation({
    mutationFn: (v: { id: number; on: boolean }) => setPredefinedMessageActive(v.id, v.on),
    onSuccess: reload,
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Predefined messages" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  const messages: PredefinedMessageRow[] = list.data?.messages ?? []

  return (
    <div>
      <h4>
        <strong>Predefined messages</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage predefined <strong>chat messages</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add message
      </button>
      <hr />

      <div className="panel-group" id="accordion" role="tablist">
        {list.error ? (
          <LoadFailed what="The predefined messages" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : messages.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No predefined messages.
          </div>
        ) : (
          messages.map((message) => (
            <div className="panel panel-default p-0" key={message.id}>
              <div className="panel-heading p-1" role="tab">
                <h4 className="panel-title font-12 d-flex">
                  <div className="col-sm-10">
                    <a
                      style={{ padding: '0 5px', cursor: 'pointer' }}
                      role="button"
                      onClick={() => setOpen(open === message.id ? null : message.id)}
                    >
                      <i
                        className="fas fa-circle"
                        style={{ color: message.active === 1 ? 'green' : 'red' }}
                        title={message.active === 1 ? 'Active' : 'Inactive'}
                      ></i>{' '}
                      {message.message}
                    </a>
                  </div>
                  <div className="col-sm-1">
                    <span className={message.company !== '' ? 'label label-info' : 'label label-default'}>
                      {message.company !== '' ? message.company : 'All companies'}
                    </span>
                  </div>
                  <div className="col-sm-1">
                    <button
                      type="button"
                      className={message.active === 1 ? 'btn btn-xs btn-danger' : 'btn btn-xs btn-primary'}
                      disabled={flip.isPending}
                      onClick={() => flip.mutate({ id: message.id, on: message.active !== 1 })}
                    >
                      {message.active === 1 ? 'Remove' : 'Activate'}
                    </button>
                  </div>
                </h4>
              </div>
              {open === message.id && (
                <div className="panel-collapse" role="tabpanel" data-message={message.id}>
                  <Translations message={message.id} onSaved={reload} />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <NewMessageModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={reload}
        companies={shell.data.companies}
        languages={shell.data.languages}
      />
    </div>
  )
}
